import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import districts from "../data/apDistricts.json";
import { colleges } from "../data/mock";

// District -> college slug mapping
const collegeByDistrict = {
  "Eluru": "eluru",
  "Krishna": "machilipatnam",
  "Nandyal": "nandyal",
  "Alluri Sitharama Raju": "paderu",
  "Palnadu": "piduguralla",
  "East Godavari": "rajamahendravaram",
  "Vizianagaram": "vizianagaram",
};

// Reference (apchfw dashboard) multi-colour district palette
const districtColors = {
  "Parvathipuram Manyam": "#E91E8C", "Srikakulam": "#16A34A", "Vizianagaram": "#3B82F6", "Alluri Sitharama Raju": "#22C55E",
  "Visakhapatnam": "#F97316", "Anakapalli": "#7C3AED", "Kakinada": "#FB923C", "East Godavari": "#2563EB", "Eluru": "#DC2626",
  "Dr. B.R. Ambedkar Konaseema": "#0EA5E9", "NTR": "#A855F7", "West Godavari": "#F59E0B", "Krishna": "#65A30D", "Palnadu": "#7C3AED",
  "Guntur": "#84CC16", "Bapatla": "#EF4444", "Prakasam": "#F472B6", "Kurnool": "#EF4444", "Nandyal": "#06B6D4",
  "Ananthapuramu": "#6D28D9", "YSR Kadapa": "#FACC15", "Sri Sathya Sai": "#EC4899", "Sri Potti Sriramulu Nellore": "#84CC16",
  "Annamayya": "#22C55E", "Tirupati": "#F97316", "Chittoor": "#8B5CF6",
};
const palette = ["#F97316", "#FACC15", "#FB923C", "#EF4444", "#22C55E", "#3B82F6"];

const small = new Set(["Kakinada", "East Godavari", "Dr. B.R. Ambedkar Konaseema", "West Godavari", "Krishna", "Guntur", "Bapatla", "NTR", "Visakhapatnam", "Anakapalli", "Vizianagaram"]);
const shortName = (n) => n
  .replace("Sri Potti Sriramulu Nellore", "SPSR Nellore")
  .replace("Dr. B.R. Ambedkar Konaseema", "Konaseema")
  .replace("Alluri Sitharama Raju", "Alluri Sitharama Raju")
  .replace("Parvathipuram Manyam", "Parvathipuram Manyam");

export default function APMap({ compact = false, highlight = null, onHover }) {
  const [hoverState, setHover] = useState(null);
  const hover = hoverState || highlight;
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const nav = useNavigate();

  const items = useMemo(() => districts.map((d, i) => ({
    ...d,
    color: districtColors[d.name] || palette[i % palette.length],
    college: colleges.find(c => c.slug === collegeByDistrict[d.name]),
  })), []);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    setPos({ x: e.clientX - r.left, y: e.clientY - r.top });
  };

  return (
    <div data-testid="ap-district-map" style={{ position: "relative", width: "100%" }} onMouseMove={onMove}>
      <svg viewBox="0 0 820 680" style={{ width: "100%", height: "auto", display: "block", overflow: "visible" }}>
        <defs>
          <filter id="mapShadow" x="-10%" y="-10%" width="120%" height="130%">
            <feDropShadow dx="0" dy="10" stdDeviation="10" floodColor="#C2410C" floodOpacity="0.28" />
          </filter>
          <filter id="pinGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="2.5" result="b" />
            <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
          <pattern id="dots" width="14" height="14" patternUnits="userSpaceOnUse">
            <circle cx="2" cy="2" r="1.4" fill="rgba(249,115,22,0.18)" />
          </pattern>
          <radialGradient id="pinG" cx="0.35" cy="0.3">
            <stop offset="0%" stopColor="#FEF3C7" />
            <stop offset="45%" stopColor="#F97316" />
            <stop offset="100%" stopColor="#B91C1C" />
          </radialGradient>
        </defs>

        <ellipse cx="420" cy="380" rx="400" ry="320" fill="url(#dots)" />

        <g filter="url(#mapShadow)" style={{ transformOrigin: "410px 340px", animation: "fadeIn 800ms ease-out both" }}>
          {items.map((d, i) => {
            const active = hover === d.name;
            return (
              <path
                key={d.name}
                data-testid={`district-${d.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                d={d.d}
                fill={d.color}
                stroke="#fff"
                strokeWidth={active ? 2.4 : 1.4}
                strokeLinejoin="round"
                onMouseEnter={() => { setHover(d.name); onHover && onHover(d.name); }}
                onMouseLeave={() => { setHover(null); onHover && onHover(null); }}
                onClick={() => d.college && nav(`/colleges/${d.college.slug}`)}
                style={{
                  cursor: d.college ? "pointer" : "default",
                  transition: "filter 250ms, transform 250ms, opacity 250ms",
                  filter: active ? "brightness(1.12) saturate(1.2) drop-shadow(0 6px 10px rgba(194,65,12,0.35))" : "none",
                  transform: active ? "translateY(-3px)" : "none",
                  opacity: hover && !active ? 0.82 : 1,
                  animation: `fadeUp 600ms ease-out ${i * 25}ms both`,
                }}
              />
            );
          })}
        </g>

        {/* Labels */}
        {!compact && items.map((d) => (
          <text
            key={`t-${d.name}`}
            x={d.cx}
            y={d.cy + (d.college ? 14 : 3)}
            textAnchor="middle"
            fontSize={small.has(d.name) ? 7.5 : d.name.length > 14 ? 9 : 10.5}
            fontWeight="700"
            fill="#1C1917"
            stroke="#fff"
            strokeWidth="2.6"
            paintOrder="stroke"
            style={{ pointerEvents: "none", fontFamily: "Poppins, sans-serif" }}
          >
            {shortName(d.name)}
          </text>
        ))}

        {/* College pins */}
        {items.filter(d => d.college).map((d, i) => (
          <g key={`p-${d.name}`} style={{ pointerEvents: "none", transformOrigin: `${d.cx}px ${d.cy}px`, animation: `apPinPop 600ms cubic-bezier(0.2,0.9,0.2,1.3) ${0.6 + i * 0.1}s both` }}>
            <circle cx={d.cx} cy={d.cy} r="16" fill="none" stroke="#DC2626" strokeWidth="2" style={{ transformOrigin: `${d.cx}px ${d.cy}px`, animation: `apRing 2.2s ease-out ${i * 0.3}s infinite` }} />
            <g transform={`translate(${d.cx}, ${d.cy - 4})`}>
              <path d="M0 -18 C -9 -18 -12 -10 -12 -6 C -12 2 0 14 0 14 C 0 14 12 2 12 -6 C 12 -10 9 -18 0 -18 Z" fill="url(#pinG)" stroke="#fff" strokeWidth="1.6" filter="url(#pinGlow)" />
              <circle cx="0" cy="-7" r="4" fill="#fff" />
              <circle cx="0" cy="-7" r="1.8" fill="#DC2626" />
            </g>
          </g>
        ))}
      </svg>

      {hoverState && (
        <div
          data-testid="map-tooltip"
          className="glass"
          style={{
            position: "absolute", left: Math.min(pos.x + 16, 420), top: pos.y - 10,
            padding: "10px 14px", borderRadius: 12, pointerEvents: "none", zIndex: 5, minWidth: 180,
            border: "1px solid rgba(249,115,22,0.35)",
          }}
        >
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1.2, color: "#EA580C" }}>DISTRICT</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#1C1917" }}>{hover}</div>
          {(() => {
            const d = items.find(x => x.name === hover);
            return d?.college ? (
              <div style={{ marginTop: 6, paddingTop: 6, borderTop: "1px dashed rgba(249,115,22,0.35)" }}>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: "#C2410C" }}>{d.college.name}</div>
                <div style={{ fontSize: 11.5, color: "#78716C", marginTop: 2 }}>{d.college.seats} MBBS seats · Est. {d.college.established}</div>
                <div style={{ fontSize: 11, color: "#DC2626", fontWeight: 700, marginTop: 4 }}>Click to view college →</div>
              </div>
            ) : (
              <div style={{ fontSize: 11.5, color: "#78716C", marginTop: 4 }}>No APMERCL medical college yet</div>
            );
          })()}
        </div>
      )}

      <style>{`
        @keyframes apPinPop { 0% { transform: translateY(-14px) scale(0); opacity: 0; } 70% { transform: translateY(2px) scale(1.05); opacity: 1; } 100% { transform: translateY(0) scale(1); opacity: 1; } }
        @keyframes apRing { 0% { transform: scale(0.3); opacity: 0.9; } 100% { transform: scale(1.6); opacity: 0; } }
      `}</style>
    </div>
  );
}
